import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { AuthState, LoginCredentials, UserCreate, UserUpdate } from "../types";
import { userService } from "../services/userService";
import {
  getAccessToken,
  getErrorMessage,
  onAuthenticationExpired,
  restoreAccessToken,
  setAccessToken,
} from "../services/api";

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (userData: UserCreate) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  updateCurrentUser: (userData: UserUpdate) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const logoutStartedRef = useRef(false);
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const fetchUser = useCallback(async () => {
    try {
      await restoreAccessToken();
      const user = await userService.getCurrentUser();
      setState({
        user,
        token: getAccessToken(),
        isAuthenticated: true,
        isLoading: false,
      });
      logoutStartedRef.current = false;
    } catch {
      setAccessToken(null);
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await userService.login(credentials);
    setAccessToken(response.access_token);

    const user = await userService.getCurrentUser();
    setState({
      user,
      token: response.access_token,
      isAuthenticated: true,
      isLoading: false,
    });
    logoutStartedRef.current = false;
  }, []);

  const register = useCallback(
    async (userData: UserCreate) => {
      await userService.register(userData);
      // Auto-login after registration
      await login({ username: userData.username, password: userData.password });
    },
    [login]
  );

  const logout = useCallback(async () => {
    if (logoutStartedRef.current) return;
    logoutStartedRef.current = true;
    try {
      await userService.logout();
    } finally {
      setAccessToken(null);
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  const updateCurrentUser = useCallback(async (userData: UserUpdate) => {
    try {
      const user = await userService.updateCurrentUser(userData);
      setState((previousState) => ({ ...previousState, user }));
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => onAuthenticationExpired(() => void logout()), [logout]);

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    fetchUser,
    updateCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
